import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { IResident } from '@/types/api/traversal/getNursingHomeResidents';
import { IResidentCareRecord } from '@/types/api/traversal/getNursingHomeCareRecord';
import { IMdResident } from '@/types/api/medical/getResidents';
import { SortType } from '@/types/components';
import { filterResidentList } from '@/utils/residentCategories';
import { sortResidentList } from '@/utils/residentSort';
import { useInterval } from './useInterval';

export type ResidentListStatus = 'loading' | 'success' | 'error';
type Resident = IResident | IResidentCareRecord | IMdResident;

interface Options<T extends Resident, D extends { residents: T[] }> {
  // アカウント・施設・権限・サービス・取得期間を含める。表示条件は含めない。
  scope: string;
  enabled: boolean;
  autoFetch?: boolean;
  fetchData: () => Promise<D>;
  categories: string[];
  sortType: SortType;
  permittedUnitId: number | null;
  onLoaded?: (residents: T[]) => void;
}

export const useResidentList = <T extends Resident, D extends { residents: T[] }>({
  scope, enabled, autoFetch = true, fetchData, categories, sortType, permittedUnitId, onLoaded,
}: Options<T, D>) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [source, setSource] = React.useState<{ scope: string; status: ResidentListStatus; data?: D }>();
  const requestId = React.useRef(0);
  const mounted = React.useRef(false);
  const latest = React.useRef({ scope, fetchData, categories, permittedUnitId, onLoaded, t, showSnackbar });
  React.useEffect(() => {
    latest.current = { scope, fetchData, categories, permittedUnitId, onLoaded, t, showSnackbar };
  }, [scope, fetchData, categories, permittedUnitId, onLoaded, t, showSnackbar]);

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current += 1; };
  }, []);

  const refresh = React.useCallback(async () => {
    if (!enabled || !mounted.current || latest.current.scope !== scope) return;
    const currentRequest = ++requestId.current;
    setSource((previous) => ({ scope, status: 'loading', data: previous?.scope === scope ? previous.data : undefined }));
    try {
      const data = await latest.current.fetchData();
      if (currentRequest !== requestId.current || !mounted.current) return;
      setSource({ scope, status: 'success', data });
      const current = latest.current;
      current.onLoaded?.(filterResidentList(data.residents, current.categories, current.permittedUnitId));
      return data;
    } catch (error) {
      if (currentRequest !== requestId.current || !mounted.current) return;
      // 更新失敗時は同じ取得範囲の直前のデータを保持する。
      setSource((previous) => ({ scope, status: 'error', data: previous?.scope === scope ? previous.data : undefined }));
      console.error('fetchResidentList error', error);
      latest.current.showSnackbar(latest.current.t('@failedToLoadResidents', '利用者一覧を取得できませんでした'), 'error');
    }
  }, [enabled, scope]);

  React.useEffect(() => {
    if (autoFetch) void refresh();
    // 範囲変更・無効化・アンマウント後は、進行中の取得結果を反映しない。
    return () => { requestId.current += 1; };
  }, [refresh, autoFetch]);
  useInterval(refresh, enabled && autoFetch ? 5 * 60 * 1000 : null);

  const data = enabled && source?.scope === scope ? source.data : undefined;
  const status: ResidentListStatus = enabled && source?.scope === scope ? source.status : 'loading';
  const residents = React.useMemo(() => sortResidentList(
    filterResidentList(data?.residents ?? [], categories, permittedUnitId), sortType,
  ), [data, categories, permittedUnitId, sortType]);

  return { data, residents, status, refresh };
};
